'use client';

import { CartItem } from '@/types/Cart';
import { AddedToCartData, RecentlyViewedItemData, StartedCheckoutData, ViewedProductData } from '@/types/Klaviyo';
import { ProductStrapi } from '@/types/Product';

export const mapDataToAddedToCart = (
  _cart: CartItem[],
  cartItem?: CartItem | null,
  hostname?: string
  // eslint-disable-next-line sonarjs/cognitive-complexity
): AddedToCartData => {
  const cartProduct = cartItem || _cart[0] || {};
  const _hostname = hostname;

  const klaviyoData: AddedToCartData = {
    $event_id: `${new Date().valueOf()}_${Date.now()}`,
    AddedItemCategories: cartItem
      ? cartProduct.category
        ? [cartProduct.category]
        : []
      : (_cart || []).map(product => (product.category ? product.category : '')),
    AddedItemPrice: cartProduct.price,
    AddedItemProductID: cartProduct.id,
    AddedItemImageURL: cartProduct.image,
    AddedItemProductName: cartProduct.name,
    AddedItemQuantity: cartProduct.quantity,
    AddedItemSKU: cartProduct.id,
    AddedItemURL: `${_hostname}/product/${encodeURIComponent(cartProduct.name)}`,
    CheckoutURL: `${_hostname}/checkout/preorder`,
    ItemNames: cartItem ? [cartProduct.name] : (_cart || []).map(product => product.name),
    Items: cartItem
      ? [
          {
            Brand: 'pawTree',
            Categories: cartProduct.category ? [cartProduct.category] : [],
            CompareAtPrice: cartProduct.price,
            ImageURL: cartProduct.image,
            Price: cartProduct.price,
            ProductID: `${cartProduct.id}`,
            ProductName: cartProduct.name,
            SKU: `${cartProduct.id}`,
            URL: `${_hostname}/product/${encodeURIComponent(cartProduct.name)}`,
          },
        ]
      : (_cart || []).map(product => ({
          Brand: 'pawTree',
          Categories: product.category ? [product.category] : [],
          CompareAtPrice: product.price,
          ImageURL: product.image,
          Price: product.price,
          ProductID: `${product.id}`,
          ProductName: product.name,
          SKU: `${product.id}`,
          URL: `${_hostname}/product/${encodeURIComponent(product.name)}`,
        })),
    $value: cartItem
      ? +(cartProduct.price ?? 0) * (cartProduct.quantity ?? 1)
      : _cart?.reduce((acc, val) => +acc + +((+val.price || 0) * (val.quantity ?? 1)), 0) || 0,
  };
  return klaviyoData;
};

export const mapDataToViewedProduct = (product: ProductStrapi, hostname?: string | null): ViewedProductData => {
  const price = product?.attributes?.Prices?.RetailPrice ?? 0;
  const compareAtPrice = product?.attributes?.Prices?.RetailStrikeOutPrice ?? 0;
  const klaviyoData: ViewedProductData = {
    Brand: 'pawTree',
    Categories: product.attributes.PetCategory ? [product.attributes.PetCategory] : [],
    CompareAtPrice: compareAtPrice,
    ImageURL: product.attributes.MainImage,
    Price: price,
    ProductID: `${product.id}`,
    ProductName: product.attributes.ItemName,
    SKU: `${product.id}`,
    URL: `${hostname}/${globalThis?.window?.PAWTREE_CURRENT_WEBALIAS || 'pawtree'}/product/${
      product.attributes.Slug || product.attributes.ItemCode
    }`,
  };

  return klaviyoData;
};

export const mapDataToRecentlyViewedItem = (
  product: ProductStrapi,
  hostname?: string | null
): RecentlyViewedItemData => {
  const price = product?.attributes?.Prices?.RetailPrice ?? 0;
  const compareAtPrice = product?.attributes?.Prices?.RetailStrikeOutPrice ?? 0;

  const recentlyViewedData: RecentlyViewedItemData = {
    Title: product.attributes.ItemName,
    ItemId: `${product.id}`,
    Categories: product.attributes.PetCategory ? [product.attributes.PetCategory] : [],
    ImageUrl: product.attributes.MainImage,
    Url: `${hostname}/${globalThis?.window?.PAWTREE_CURRENT_WEBALIAS || 'pawtree'}/product/${
      product.attributes.Slug || product.attributes.ItemCode
    }`,
    Metadata: {
      Brand: 'pawTree',
      Price: price,
      CompareAtPrice: compareAtPrice,
    },
  };

  return recentlyViewedData;
};

export const mapDataToStartedCheckout = (
  cart: CartItem[],
  hostname?: string,
  checkoutPath?: string
): StartedCheckoutData => {
  const _hostname = hostname;
  const Items = [
    ...(cart || []).map(product => ({
      Brand: 'pawTree',
      Categories: product.category ? [product.category] : [],
      CompareAtPrice: product.price,
      ImageURL: product.image,
      Price: product.price,
      ProductID: `${product.id}`,
      ProductName: product.name,
      SKU: `${product.id}`,
      URL: `${_hostname}/product/${encodeURIComponent(product.name)}`,
    })),
  ];
  const ItemNames = [...(cart || []).map(product => product.name)];
  const $value =
    cart?.reduce((acc, val) => {
      const totalPrice = typeof val.totalPrice !== 'undefined' ? +val.totalPrice : +val.price;
      return acc + totalPrice;
    }, 0) || 0;
  const Categories = [...(cart || []).map(product => (product.category ? product.category : ''))];
  const CheckoutURL = `${_hostname}${checkoutPath || '/checkout/preorder'}`;
  const klaviyoStartedCheckoutData: StartedCheckoutData = {
    Items,
    ItemNames,
    $value,
    Categories,
    CheckoutURL,
    $event_id: `${new Date().valueOf()}_${Date.now()}`,
  };

  return klaviyoStartedCheckoutData;
};

/**
 * Helper to track product views with both standard track event and recently viewed items
 * This should be called on product detail pages
 */
export const trackProductView = async (product: ProductStrapi, hostname?: string | null) => {
  if (typeof window === 'undefined' || !window.klaviyo) {
    return;
  }

  try {
    // Track the Viewed Product event
    const viewedProductData = mapDataToViewedProduct(product, hostname);
    await window.klaviyo.push(['Viewed Product', viewedProductData]);

    // Track for Recently Viewed Items feature
    const recentlyViewedData = mapDataToRecentlyViewedItem(product, hostname);
    await window.klaviyo.push(['Recently Viewed Items', recentlyViewedData]);
  } catch (error) {
    console.error('Error tracking product view:', error);
  }
};
